import { useCallback, useEffect, useMemo, useState } from 'react';
import { conDosPasosEnElMomento, pideDosPasosEnElMomento } from '../../api/dosPasosEnElMomento';
import type { CategoriaActividad, ConsultaActividad, EntradaActividad, ListaActividad, PermisoSoporte } from '../../api/ops/actividad';
import type { ListaMiembros } from '../../api/operaciones';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { iniciales } from '../../logica/navegacion';
import { usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { usePedirCodigoDosPasos } from '../../marco/dosPasos';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import {
    AYUDA,
    CATEGORIAS,
    DURACIONES,
    ETIQUETA_CATEGORIA,
    METODO_ENTRADA,
    PERIODOS,
    SUPERFICIE,
    TEXTOS,
    cambiosDe,
    cuandoPermiso,
    desdeDe,
    etiquetaDia,
    fraseDe,
    hastaTexto,
    horaDe,
    lineaDuracion,
    porDias,
    textoFrase,
    type Periodo,
} from './actividad';
import './actividad.css';

/**
 * Actividad (1.19): registro por días de lo que pasa en el negocio (`listActivity`), con filtros por categoría (G183),
 * búsqueda (`q`), persona y periodo; ficha de cada entrada (Resumen · Cambios · Origen) en RV Panel; los accesos de
 * Soporte destacados; el permiso de cambios a Soporte (G73: lo da y lo retira solo el propietario, 1, 4 o 24 h, con el
 * código de dos pasos en el momento si hace falta, G116); exportar (`exportActivity`); «Anterior / Siguiente» (G121).
 * Con ámbito «lo suyo» (Encargado) la API solo da la operativa: sin filtros por categoría ni permiso de Soporte.
 */
type TabFicha = 'resumen' | 'cambios' | 'origen';
const TABS_FICHA: readonly { clave: TabFicha; etiqueta: string; icono: 'file-text' | 'git-compare' | 'fingerprint' }[] = [
    { clave: 'resumen', etiqueta: 'Resumen', icono: 'file-text' },
    { clave: 'cambios', etiqueta: 'Cambios', icono: 'git-compare' },
    { clave: 'origen', etiqueta: 'Origen', icono: 'fingerprint' },
];

const PERMISO_CONCEDER = 'negocio.soporte_escritura.conceder';

export function Actividad({ ops, negocio, movil, onSesionCaducada, busqueda }: PropsPantalla) {
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const pedirCodigo = usePedirCodigoDosPasos();
    const limitada = permisos.ambito('actividad.ver') === 'own';
    const puedeConceder = permisos.puede(PERMISO_CONCEDER);
    const veEquipo = permisos.puede('equipo.miembros.ver');

    const [ahora] = useState(() => new Date());
    // `?categoria=soporte` abre la pestaña «Soporte» (G183): lo usa la tarjeta de Soporte y los enlaces de los avisos.
    const [categoria, setCategoria] = useState<CategoriaActividad | null>(() => {
        const pedida = busqueda.get('categoria');
        return pedida === 'soporte' || pedida === 'support' ? 'support' : null;
    });
    const [texto, setTexto] = useState('');
    const [q, setQ] = useState('');
    const [persona, setPersona] = useState('');
    const [periodo, setPeriodo] = useState<Periodo>('7');
    const pag = usePaginacionCursor();
    const [lista, setLista] = useState<ListaActividad | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [miembros, setMiembros] = useState<ListaMiembros['data']>([]);
    const [abierta, setAbierta] = useState<EntradaActividad | null>(null);
    const [tab, setTab] = useState<TabFicha>('resumen');
    const [vigente, setVigente] = useState<PermisoSoporte | null>(null);
    const [anteriores, setAnteriores] = useState<PermisoSoporte[]>([]);
    const [ventana, setVentana] = useState<'permitir' | 'exportar' | 'exportada' | null>(null);
    const [horas, setHoras] = useState<1 | 4 | 24>(4);
    const [nota, setNota] = useState('');
    const [conNota, setConNota] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    const { reiniciar } = pag;
    // La búsqueda se manda al dejar de escribir.
    useEffect(() => {
        const limpia = texto.trim();
        if (limpia === q) {
            return;
        }
        const t = setTimeout(() => {
            setQ(limpia);
            reiniciar();
        }, 300);
        return () => {
            clearTimeout(t);
        };
    }, [texto, q, reiniciar]);

    const filtro = useMemo<ConsultaActividad>(
        () => ({
            ...(categoria !== null && !limitada ? { category: categoria } : {}),
            ...(q !== '' ? { q } : {}),
            ...(persona !== '' ? { member_id: persona } : {}),
            from: desdeDe(periodo, ahora),
        }),
        [categoria, limitada, q, persona, periodo, ahora],
    );

    const fallo = useCallback(
        (e: unknown, texto: string) => {
            if (e instanceof ErrorApi && e.codigo === 'unauthenticated') {
                onSesionCaducada();
                return;
            }
            avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? texto });
        },
        [avisar, onSesionCaducada],
    );

    useEffect(() => {
        let vivo = true;
        ops.listActivity(negocio, filtro, { cursor: pag.cursor })
            .then((l) => {
                if (vivo) {
                    setError(null);
                    setLista(l);
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.codigo === 'unauthenticated') {
                    onSesionCaducada();
                } else {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, filtro, pag.cursor, intento, onSesionCaducada]);

    // Personas del filtro (si puede ver el equipo).
    useEffect(() => {
        if (!veEquipo) {
            return;
        }
        let vivo = true;
        ops.listMembers(negocio, { limit: 100 })
            .then((l) => {
                if (vivo) {
                    setMiembros(l.data);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, veEquipo]);

    // Permisos de cambios de Soporte: el vigente y los anteriores (no con solo la operativa).
    const cargarPermisos = useCallback(() => {
        if (limitada) {
            return;
        }
        ops.listSupportWriteGrants(negocio, 'active')
            .then((l) => {
                setVigente(l.data.find((g) => g.status === 'active') ?? null);
            })
            .catch(() => undefined);
        ops.listSupportWriteGrants(negocio, 'ended', { limit: 5 })
            .then((l) => {
                setAnteriores(l.data);
            })
            .catch(() => undefined);
    }, [ops, negocio, limitada]);
    useEffect(cargarPermisos, [cargarPermisos]);

    const abrirExportar = useCallback(() => {
        setVentana('exportar');
    }, []);
    useCabecera({ ayuda: AYUDA.lista, acciones: [{ clave: 'exportar', texto: TEXTOS.exportar, icono: 'download', onClick: abrirExportar }] });

    const nombreDe = (miembro: string | null | undefined) => miembros.find((m) => m.id === miembro)?.display_name ?? null;
    const recargar = () => {
        setIntento((i) => i + 1);
    };
    const cambiarFiltro = (cambio: () => void) => {
        cambio();
        reiniciar();
    };
    const quitarFiltros = () => {
        cambiarFiltro(() => {
            setCategoria(null);
            setTexto('');
            setQ('');
            setPersona('');
            setPeriodo('7');
        });
    };

    const permitir = () => {
        setOcupado(true);
        const datos = { duration_minutes: horas * 60, ...(nota.trim() !== '' ? { note: nota.trim() } : {}) };
        conDosPasosEnElMomento(ops, pedirCodigo, (clave) => ops.createSupportWriteGrant(negocio, datos, clave))
            .then((g) => {
                setVigente(g);
                setVentana(null);
                recargar();
            })
            .catch((e: unknown) => {
                // Si se cancela la ventana del código, no hay nada que contar.
                if (!pideDosPasosEnElMomento(e)) {
                    fallo(e, TEXTOS.errorPermitir);
                }
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    // Retirar es inmediato y sin «Deshacer»: es una medida de seguridad (código del diseño de 1.19).
    const retirar = () => {
        if (vigente === null) {
            return;
        }
        setOcupado(true);
        ops.revokeSupportWriteGrant(negocio, vigente.id)
            .then(() => {
                setVigente(null);
                avisar({ tipo: 'ok', texto: TEXTOS.retirado });
                cargarPermisos();
                recargar();
            })
            .catch((e: unknown) => {
                fallo(e, TEXTOS.errorRetirar);
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const exportar = () => {
        setOcupado(true);
        // Se exporta lo que se ve (G334): los mismos filtros que la consulta, también la pestaña y la búsqueda.
        ops.exportActivity(negocio, filtro)
            .then(() => {
                setVentana('exportada');
                recargar();
            })
            .catch((e: unknown) => {
                fallo(e, TEXTOS.errorExportar);
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const nombrePersona = persona === '' ? 'Todas las personas' : (nombreDe(persona) ?? '');
    const nombrePeriodo = PERIODOS.find((p) => p.clave === periodo)?.etiqueta ?? '';
    const hayFiltros = categoria !== null || q !== '' || persona !== '' || periodo !== '7';

    const herramientas = (
        <div className="rv-act-herr">
            {limitada ? (
                <div className="rv-act-limitada">
                    <Icono nombre="eye" />
                    {TEXTOS.soloOperativa}
                </div>
            ) : (
                <div role="radiogroup" aria-label="Tipo de actividad" className="rv-act-filtros">
                    {CATEGORIAS.map((c) => (
                        <button
                            key={c.etiqueta}
                            type="button"
                            role="radio"
                            aria-checked={categoria === c.clave}
                            className="rv-act-filtro"
                            onClick={() => {
                                cambiarFiltro(() => {
                                    setCategoria(c.clave);
                                });
                            }}
                        >
                            {c.etiqueta}
                        </button>
                    ))}
                </div>
            )}
            <div className="rv-act-ajustes">
                <div className="rv-act-buscar">
                    <Campo etiqueta={TEXTOS.buscar} tipo="search" valor={texto} onChange={setTexto} nombre="buscar-actividad" />
                </div>
                {miembros.length > 0 && (
                    <label className="rv-act-select">
                        <span className="rv-act-oculto">Persona</span>
                        <Icono nombre="user" />
                        <select
                            value={persona}
                            onChange={(e) => {
                                const v = e.target.value;
                                cambiarFiltro(() => {
                                    setPersona(v);
                                });
                            }}
                        >
                            <option value="">Todas las personas</option>
                            {miembros.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.display_name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                <label className="rv-act-select">
                    <span className="rv-act-oculto">Periodo</span>
                    <Icono nombre="calendar-days" />
                    <select
                        value={periodo}
                        onChange={(e) => {
                            const v = e.target.value as Periodo;
                            cambiarFiltro(() => {
                                setPeriodo(v);
                            });
                        }}
                    >
                        {PERIODOS.map((p) => (
                            <option key={p.clave} value={p.clave}>
                                {p.etiqueta}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
        </div>
    );

    const lineaVigente =
        vigente === null
            ? puedeConceder
                ? TEXTOS.paraCambios
                : TEXTOS.soloPropietario
            : [
                  `Lo permitió ${nombreDe(vigente.granted_by_member_id) ?? 'el propietario'} ${etiquetaDia(new Date(vigente.granted_at), new Date()).toLocaleLowerCase('es')} a las ${horaDe(new Date(vigente.granted_at))}`,
                  vigente.note ?? '',
              ]
                  .filter((p) => p !== '')
                  .join(' · ');

    const tarjetaSoporte = !limitada && categoria === 'support' && (
        <div className="rv-act-soporte">
            <div className="rv-act-soporte__tarjeta">
                <span className="rv-act-soporte__llave">
                    <Icono nombre="key-round" />
                </span>
                <span className="rv-act-soporte__textos">
                    <strong>{vigente === null ? TEXTOS.soloConsulta : `Soporte puede hacer cambios hasta ${hastaTexto(new Date(vigente.expires_at), new Date())}`}</strong>
                    <span>{lineaVigente}</span>
                </span>
                {puedeConceder && vigente === null && (
                    <Boton
                        icono="key-round"
                        movil={movil}
                        onClick={() => {
                            setHoras(4);
                            setNota('');
                            setConNota(false);
                            setVentana('permitir');
                        }}
                    >
                        {TEXTOS.permitir}
                    </Boton>
                )}
                {puedeConceder && vigente !== null && (
                    <Boton tipo="secundario" icono="x" movil={movil} ocupado={ocupado} ocupadoTexto="Retirando…" onClick={retirar}>
                        {TEXTOS.retirar}
                    </Boton>
                )}
            </div>
            {anteriores.length > 0 && (
                <details className="rv-act-anteriores">
                    <summary>
                        <Icono nombre="history" />
                        <span>{TEXTOS.anteriores}</span>
                        <span className="rv-act-anteriores__n">{anteriores.length === 1 ? '1 permiso anterior' : `${String(anteriores.length)} permisos anteriores`}</span>
                    </summary>
                    {anteriores.map((g) => (
                        <div key={g.id} className="rv-act-anterior">
                            <span>
                                <strong>{cuandoPermiso(g.granted_at, g.revoked_at ?? g.expires_at, new Date())}</strong>
                                <span>{`Lo permitió ${nombreDe(g.granted_by_member_id) ?? 'el propietario'}${g.status === 'revoked' ? ` · lo retiró ${nombreDe(g.revoked_by_member_id) ?? 'el propietario'}` : ''}`}</span>
                            </span>
                            <Chip estado={g.status === 'expired' ? 'problem' : 'neutral'}>{g.status === 'expired' ? 'Caducado' : 'Retirado'}</Chip>
                        </div>
                    ))}
                </details>
            )}
        </div>
    );

    const cuerpo = (() => {
        if (error !== null) {
            return <EstadoError seccion="la actividad" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
        }
        if (lista === null) {
            return <EstadoCargando seccion="la actividad" />;
        }
        if (lista.data.length === 0) {
            return (
                <div className="rv-act-vacia">
                    <span className="rv-act-vacia__icono">
                        <Icono nombre="inbox" />
                    </span>
                    <strong>{TEXTOS.vacia}</strong>
                    <span>{TEXTOS.vaciaSub}</span>
                    {hayFiltros && (
                        <Boton tipo="secundario" icono="rotate-ccw" onClick={quitarFiltros}>
                            {TEXTOS.quitarFiltros}
                        </Boton>
                    )}
                </div>
            );
        }
        return (
            <>
                {porDias(lista.data, new Date()).map((dia) => (
                    <section key={dia.etiqueta} className="rv-act-dia" aria-label={dia.etiqueta}>
                        <h2>{dia.etiqueta}</h2>
                        <div className="rv-act-lista">
                            {dia.entradas.map((e) => {
                                const f = fraseDe(e);
                                const sup = SUPERFICIE[e.surface];
                                const cuando = horaDe(new Date(e.occurred_at));
                                const conMotivo = e.reason !== undefined && e.reason !== null && e.reason !== '';
                                return (
                                    <button
                                        key={e.id}
                                        type="button"
                                        className={`rv-act-fila${abierta?.id === e.id ? ' rv-act-fila--abierta' : ''}${e.actor.type === 'platform' ? ' rv-act-fila--soporte' : ''}`}
                                        onClick={() => {
                                            setAbierta(e);
                                            setTab('resumen');
                                        }}
                                    >
                                        {!movil && <span className="rv-act-fila__hora">{cuando}</span>}
                                        <Baldosa entrada={e} />
                                        <span className="rv-act-fila__textos">
                                            <span className="rv-act-fila__frase">
                                                <strong>{f.actor}</strong> {f.codigo ? <code>{f.verbo}</code> : f.verbo} {f.objeto !== '' && <strong>{f.objeto}</strong>}
                                            </span>
                                            <span className="rv-act-fila__linea">
                                                {movil && <span className="rv-act-fila__hora-m">{cuando}</span>}
                                                <span>
                                                    <Icono nombre={sup.icono} />
                                                    {sup.texto}
                                                </span>
                                                {conMotivo && (
                                                    <span>
                                                        <Icono nombre="message-circle" />
                                                        Con motivo
                                                    </span>
                                                )}
                                            </span>
                                        </span>
                                        <Icono nombre="chevron-right" className="rv-act-fila__chev" />
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                ))}
                <Paginacion
                    hayAnterior={pag.hayAnterior}
                    haySiguiente={lista.next_cursor !== null && lista.next_cursor !== ''}
                    onAnterior={pag.anterior}
                    onSiguiente={() => {
                        pag.siguiente(lista.next_cursor);
                    }}
                    movil={movil}
                />
                <div className="rv-act-pie">
                    <Icono nombre="lock" />
                    {TEXTOS.noSeBorran}
                </div>
            </>
        );
    })();

    return (
        <div className="rv-act">
            {herramientas}
            {tarjetaSoporte}
            {cuerpo}
            <Ficha
                entrada={abierta}
                tab={tab}
                onTab={setTab}
                movil={movil}
                onCerrar={() => {
                    setAbierta(null);
                }}
            />
            <Ventana
                abierta={ventana === 'permitir'}
                titulo={TEXTOS.permitirTitulo}
                texto={TEXTOS.permitirTexto}
                cancelar="Cancelar"
                cancelarIcono="x"
                accion={movil ? TEXTOS.permitirCorto : TEXTOS.permitir}
                accionIcono="key-round"
                ocupado={ocupado}
                ocupadoTexto="Permitiendo…"
                movil={movil}
                onAccion={permitir}
                onCerrar={() => {
                    setVentana(null);
                }}
            >
                <div className="rv-act-durante">
                    <span className="rv-act-durante__titulo">{TEXTOS.durante}</span>
                    <div role="radiogroup" aria-label={TEXTOS.durante} className="rv-act-durante__opciones">
                        {DURACIONES.map((d) => (
                            <button
                                key={d.horas}
                                type="button"
                                role="radio"
                                aria-checked={horas === d.horas}
                                onClick={() => {
                                    setHoras(d.horas);
                                }}
                            >
                                {d.etiqueta}
                            </button>
                        ))}
                    </div>
                    <span className="rv-act-durante__linea">{lineaDuracion(horas, new Date())}</span>
                </div>
                {conNota ? (
                    <Campo etiqueta={TEXTOS.nota} valor={nota} max={500} ayuda={TEXTOS.notaAyuda} onChange={setNota} />
                ) : (
                    <button
                        type="button"
                        className="rv-act-nota"
                        onClick={() => {
                            setConNota(true);
                        }}
                    >
                        <Icono nombre="plus" />
                        <span>{TEXTOS.anadirNota}</span>
                    </button>
                )}
            </Ventana>
            <Ventana
                abierta={ventana === 'exportar'}
                titulo={TEXTOS.exportarTitulo}
                texto={TEXTOS.exportarTexto}
                cancelar="Cancelar"
                cancelarIcono="x"
                accion={TEXTOS.exportar}
                accionIcono="download"
                ocupado={ocupado}
                ocupadoTexto="Exportando…"
                movil={movil}
                onAccion={exportar}
                onCerrar={() => {
                    setVentana(null);
                }}
            >
                <div className="rv-act-lineas">
                    <div>
                        <span>Persona</span>
                        <strong>{nombrePersona}</strong>
                    </div>
                    <div>
                        <span>Periodo</span>
                        <strong>{nombrePeriodo}</strong>
                    </div>
                </div>
            </Ventana>
            <Ventana
                abierta={ventana === 'exportada'}
                titulo={TEXTOS.exportadoTitulo}
                texto={TEXTOS.exportadoTexto}
                cancelar=""
                accion={TEXTOS.entendido}
                accionIcono="check"
                movil={movil}
                onAccion={() => {
                    setVentana(null);
                }}
                onCerrar={() => {
                    setVentana(null);
                }}
            />
        </div>
    );
}

function Baldosa({ entrada }: { entrada: EntradaActividad }) {
    switch (entrada.actor.type) {
        case 'member':
            return <span className="rv-act-baldosa rv-act-baldosa--miembro">{iniciales(entrada.actor.display_name)}</span>;
        case 'platform':
            return (
                <span className="rv-act-baldosa rv-act-baldosa--soporte">
                    <Icono nombre="headset" />
                </span>
            );
        case 'system':
            return (
                <span className="rv-act-baldosa rv-act-baldosa--sistema">
                    <Icono nombre="refresh-cw" />
                </span>
            );
        case 'device':
            return (
                <span className="rv-act-baldosa">
                    <Icono nombre="tablet-smartphone" />
                </span>
            );
        default:
            return (
                <span className="rv-act-baldosa">
                    <Icono nombre="user" />
                </span>
            );
    }
}

function Ficha({ entrada, tab, onTab, movil, onCerrar }: { entrada: EntradaActividad | null; tab: TabFicha; onTab: (t: TabFicha) => void; movil: boolean; onCerrar: () => void }) {
    const e = entrada;
    const f = e === null ? null : fraseDe(e);
    const cuando = e === null ? '' : `${etiquetaDia(new Date(e.occurred_at), new Date())}, ${horaDe(new Date(e.occurred_at))}`;
    const lineas: { k: string; v: string; mono?: boolean }[] = [];
    if (e !== null && f !== null) {
        if (tab === 'resumen') {
            const que = f.codigo ? f.verbo : `${f.verbo.charAt(0).toLocaleUpperCase('es')}${f.verbo.slice(1)} ${f.objeto}`;
            lineas.push({ k: 'Quién', v: f.actor }, { k: 'Qué', v: que }, { k: 'Cuándo', v: cuando });
            if (e.reason !== undefined && e.reason !== null && e.reason !== '') {
                lineas.push({ k: 'Motivo', v: e.reason });
            }
            lineas.push({ k: 'Permiso', v: e.action, mono: true });
        }
        if (tab === 'origen') {
            lineas.push({ k: 'Desde', v: SUPERFICIE[e.surface].texto });
            if (e.sign_in_method !== undefined && e.sign_in_method !== null) {
                lineas.push({ k: 'Cómo entró', v: METODO_ENTRADA[e.sign_in_method] ?? e.sign_in_method });
            }
            if (e.real_actor?.display_name !== undefined) {
                lineas.push({ k: 'Persona real', v: e.real_actor.display_name });
            }
            if (e.two_factor_confirmed_at !== undefined && e.two_factor_confirmed_at !== null) {
                lineas.push({ k: 'Dos pasos confirmados', v: horaDe(new Date(e.two_factor_confirmed_at)) });
            }
            lineas.push({ k: 'Referencia', v: e.id, mono: true });
        }
    }
    const cambios = e === null || tab !== 'cambios' ? [] : cambiosDe(e);
    const idx = Math.max(0, TABS_FICHA.findIndex((t) => t.clave === tab));
    return (
        <PanelLateral
            abierto={e !== null}
            onCerrar={onCerrar}
            movil={movil}
            tamano="ficha"
            titulo={f === null ? '' : textoFrase(f)}
            eyebrow={e === null ? undefined : ETIQUETA_CATEGORIA[e.category ?? '']}
            subtitulo={e === null ? undefined : `${cuando} · ${SUPERFICIE[e.surface].texto}`}
        >
            <div className="rv-act-ficha">
                <Segmentos
                    etiqueta="Apartados de la entrada"
                    movil={movil}
                    valor={idx}
                    items={TABS_FICHA.map((t) => ({ etiqueta: t.etiqueta, icono: t.icono }))}
                    onCambio={(i) => {
                        onTab(TABS_FICHA[i]?.clave ?? 'resumen');
                    }}
                />
                <div className="rv-act-ficha__ceja">{TABS_FICHA[idx]?.etiqueta}</div>
                {tab === 'cambios' ? (
                    cambios.length === 0 ? (
                        <div className="rv-act-ficha__nada">{TEXTOS.sinCambios}</div>
                    ) : (
                        <div className="rv-act-lineas rv-act-lineas--ficha">
                            {cambios.map((c) => (
                                <div key={c.campo}>
                                    <span>{c.campo}</span>
                                    <strong>
                                        <s>{c.antes}</s>
                                        <Icono nombre="arrow-right" />
                                        {c.despues}
                                    </strong>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <div className="rv-act-lineas rv-act-lineas--ficha">
                        {lineas.map((l) => (
                            <div key={l.k}>
                                <span>{l.k}</span>
                                <strong className={l.mono === true ? 'rv-act-mono' : undefined}>{l.v}</strong>
                            </div>
                        ))}
                    </div>
                )}
                {tab === 'origen' && (
                    <div className="rv-act-ficha__nota">
                        <Icono nombre="lock" />
                        {TEXTOS.encadenada}
                    </div>
                )}
            </div>
        </PanelLateral>
    );
}
